import { useState, useCallback } from 'react';
import { uploadCorpusData } from 'redux/actions';
import { ariTypes, fileSizeLimits, KB } from 'appConstants';
import { isValidSequenceId, formatFileSize } from 'components/Settings/helper';

/**
 * This hook to handles the following actions: file selection, validation, drag-and-drop, and upload.
 *
 * @param acceptedFileTypes - List of allowed file extensions (check appConstants.js).
 * @param setContextFiles - Callback to set context with uploaded files.
 * @param setSnackBarMessage - Callback to set a message.
 * @param dispatch - Redux dispatch function.
 * @returns Handlers and state for managing file operations.
 */
export const useFileHandlers = (
  acceptedFileTypes,
  setContextFiles,
  setSnackBarMessage,
  dispatch,
  t,
) => {
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [localError, setLocalError] = useState('');
  const [step, setStep] = useState(1);
  const [fileValidation, setFileValidation] = useState({
    isValid: true,
    oversizedFiles: [],
    totalSizeExceeded: false,
  });

  /**
   * Validates that each file in an array doesn't exceed the maximum allowed size
   * @param {File[]} files - Array of File objects to validate
   * @returns {Object} Result object with validation status and details
   */
  function validateEachFileSize(filesList) {
    const result = {
      isValid: true,
      oversizedFiles: [],
    };

    for (const file of filesList) {
      if (file.size > fileSizeLimits.EACH_FILE_SIZE_LIMIT_MB) {
        result.isValid = false;
        result.oversizedFiles.push({
          name: file.name,
          size: file.size,
          sizeInMB: (file.size / (KB * KB)).toFixed(),
        });
      }
    }

    return result;
  }

  /**
   * Validates that the total size of all files doesn't exceed the maximum allowed
   * @param {File[]} files - Array of File objects to validate
   * @returns {Object} Result object with validation status and details
   */
  function validateTotalFileSize(filesList) {
    let totalSize = 0;
    for (const file of filesList) {
      totalSize += file.size;
    }

    const result = {
      isValid: totalSize <= fileSizeLimits.MAX_FILE_SIZE_LIMIT_MB,
      totalSize,
      totalSizeInMB: (totalSize / (KB * KB)).toFixed(),
    };

    return result;
  }

  const validateAndSetFileState = useCallback(
    (filesList) => {
      const oversizedFilesValidation = validateEachFileSize(filesList);
      const totalFileSize = validateTotalFileSize(filesList);

      setFileValidation({
        isValid: oversizedFilesValidation.isValid && totalFileSize.isValid,
        oversizedFiles: oversizedFilesValidation.oversizedFiles,
        totalSizeExceeded: !totalFileSize.isValid,
      });

      if (!oversizedFilesValidation.isValid) {
        const listOfOversizedFiles = oversizedFilesValidation.oversizedFiles
          .map((file) => `${file.name} (${file.sizeInMB}MB)`)
          .join(', ');
        setLocalError(
          t('file_size_exceeded_for_files', 'File size exceeded for: {{files}}', {
            files: listOfOversizedFiles,
          }),
        );
      } else if (!totalFileSize.isValid) {
        setLocalError(
          t(
            'total_of_file_sizes_exceeds',
            'Total of file sizes exceeds the maximum allowed ({{maxSize}}MB)',
            {
              maxSize: formatFileSize(fileSizeLimits.MAX_FILE_SIZE_LIMIT_MB),
            },
          ),
        );
      } else {
        setLocalError('');
      }
    },
    [t],
  );

  /**
   * Validates the file list against the accepted file types.
   *
   * @param fileList - The list of files to validate.
   * Example of fileList:
   * [
   *   { key: '56e2fa16e4b0dd800f82f23b/document.pdf', fileName: 'document.pdf' },
   *   { key: '56e2fa16e4b0dd800f82f23b/image.jpg', fileName: 'image.jpg' }
   * ]
   * @returns {boolean} - Whether the files are valid.
   */
  const validateFileTypes = useCallback(
    (fileList) => {
      if (!fileList) {
        setLocalError(t('no_files_selected', 'No files selected'));
        return false;
      }

      const fileArray = Array.prototype.slice.call(fileList);
      const invalidFiles = fileArray.filter((file) => {
        if (!file || !file.name) {
          return true;
        }
        const extension = `.${file.name.split('.').pop().toLowerCase()}`;
        return !acceptedFileTypes.includes(extension);
      });

      if (invalidFiles.length > 0) {
        const error = invalidFiles.map((f) => f.name).join(', ');
        const errorText = t('invalid_file_type', 'Invalid file type(s): {{error}}', {
          error,
        });
        setLocalError(errorText);
        return false;
      }

      setLocalError('');
      return true;
    },
    [acceptedFileTypes, t],
  );

  /**
   * Filters out duplicate files that are already in the state in the event that the user selects the same file during upload
   *
   * @param newFiles Array of new files to add.
   * @returns Array of unique files.
   */
  const filterDuplicateFiles = useCallback(
    (newFiles) =>
      newFiles.filter(
        (newFile) => !files.some((existingFile) => existingFile.name === newFile.name),
      ),
    [files],
  );

  /**
   * Handles file drop from a drag-and-drop event.
   *
   * @param e The drag event.
   */
  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);

      const droppedFiles = e.dataTransfer.files;

      const fileTypesValid = validateFileTypes(droppedFiles);

      if (fileTypesValid) {
        const uniqueFiles = filterDuplicateFiles(Array.from(droppedFiles));

        setFiles((prevFiles) => {
          const newFilesArray = [...prevFiles, ...uniqueFiles];
          validateAndSetFileState(newFilesArray);
          return newFilesArray;
        });

        setStep(2);
      }
    },
    [validateFileTypes, filterDuplicateFiles, validateAndSetFileState],
  );

  /**
   * Handles file selection from an input element.
   *
   * @param e The change event.
   */
  const handleFileSelect = useCallback(
    (e) => {
      const selectedFiles = e.target.files;

      const fileTypesValid = validateFileTypes(selectedFiles);

      if (fileTypesValid) {
        const uniqueFiles = filterDuplicateFiles(Array.from(selectedFiles));

        setFiles((prevFiles) => {
          const newFilesArray = [...prevFiles, ...uniqueFiles];
          validateAndSetFileState(newFilesArray);
          return newFilesArray;
        });

        setStep(2);
      }
    },
    [validateFileTypes, filterDuplicateFiles, validateAndSetFileState],
  );

  /**
   * Removes a file from the state by its index.
   *
   * @param index - Index of the file to remove.
   */
  const removeFile = useCallback(
    (index) => {
      setFiles((prevFiles) => {
        const newFiles = prevFiles.filter((_, i) => i !== index);

        if (newFiles.length === 0) {
          setFileValidation({
            isValid: true,
            oversizedFiles: [],
            totalSizeExceeded: false,
          });
          setLocalError('');
        } else {
          validateAndSetFileState(newFiles);
        }

        return newFiles;
      });
    },
    [validateAndSetFileState],
  );

  /**
   * Handles uploading the files and setting the context.
   */
  const handleFileUpload = useCallback(
    async (sequenceId) => {
      try {
        const basePayload = {
          ariType: ariTypes.RECRUITING_ARI,
          files,
        };

        const payload = isValidSequenceId(sequenceId)
          ? { ...basePayload, sequenceId }
          : basePayload;

        const response = await dispatch(uploadCorpusData(payload));

        const { ariContexts } = response.payload;
        const { requestStatus } = response.meta;

        if (requestStatus === 'fulfilled' && ariContexts && ariContexts.length) {
          const filteredAriContexts = ariContexts.filter(
            (context) => context.ariType === ariTypes.RECRUITING_ARI,
          );
          const [{ files: uploadedFiles }] = filteredAriContexts;
          setContextFiles(uploadedFiles);
        }
      } catch (err) {
        setSnackBarMessage(t('failed_to_upload_source_data', 'Failed to upload source data'));
      }
    },
    [dispatch, files, setSnackBarMessage, setContextFiles, t],
  );

  return {
    files,
    isDragging,
    localError,
    step,
    setStep,
    setIsDragging,
    handleDrop,
    handleFileSelect,
    removeFile,
    handleFileUpload,
    fileValidation,
  };
};
